import { Alert, Card, Modal, icons } from '@lifebit-ai/lds'
import React from 'react'

import AutomatedChecksReRun from 'modules/resultsV2/components/AutomatedChecksReRun/AutomatedChecksReRun'
import FileActionsSkeleton from 'modules/resultsV2/components/FileActionsSkeleton/FileActionsSkeleton'
import FileContent from 'modules/resultsV2/components/FileContent/FileContent'
import { PreviewModalContent } from 'modules/resultsV2/components/FileContent/FileContents.styles'
import FileDetails from 'modules/resultsV2/components/FileDetails/FileDetails'
import FormSubmissionPreview from 'modules/resultsV2/components/FormSubmissionPreview/FormSubmissionPreview'
import ParticipantFileDetails from 'modules/resultsV2/components/ParticipantFileDetails/ParticipantFileDetails'
import ViewFormModal from 'modules/resultsV2/components/ViewFormModal/ViewFormModal'
import { type Result } from 'services/results/types'

import DataAccessRequestFormCard from './DataAccessRequestFormCard'
import ImportRequestScanDetails from './ImportRequestScanDetails'
import * as styles from './ResultMeta.styles'
import { useDataAccessRequestRelatedSection } from './hooks/useDataAccessRequestRelatedSection'
import useResultMeta from './hooks/useResultMeta'
import { isCompressedFileName } from './utils'

interface ResultMetaProps {
  result: Result
  isFile: boolean
  resultId: string
  bucket: string
  path: string
  isReadyForDownload: boolean
  isApprovalRestricted?: boolean
  isFileViewDisabled?: boolean
  isFileActionsLoading?: boolean
  isParticipantFileDownloadDisabled?: boolean
  isParticipantFileDownloadLoading?: boolean
  contents: (S3FileAsContent | S3DirAsContent)[]
}

const ResultMeta: React.FC<ResultMetaProps> = ({
  result,
  isFile,
  resultId,
  bucket,
  path,
  isReadyForDownload,
  isApprovalRestricted = false,
  isFileViewDisabled = false,
  isFileActionsLoading = false,
  isParticipantFileDownloadDisabled = false,
  isParticipantFileDownloadLoading = false,
  contents
}) => {
  const {
    displayName,
    file,
    isFileChanged,
    fileChangedAlertMessage,
    isDifferentRequester,
    differentRequesterAlertMessage,
    isAutomatedChecksToReRunVisible,
    isAutomatedChecksSkippedVisible,
    formattedFileSize,
    formattedLastModified,
    selectedFileToPreview,
    previewFileKey,
    isImportRequest,
    importRequestFileName,
    importRequestFileSize,
    importRequestScanResult,
    importRequestScanStartedAt,
    importRequestScanCompletedAt,
    importRequestScanStatusCode,
    isImportRequestScanLoading,
    customFormSubmission,
    isDataAccessRequestResult,
    participantFile,
    downloadDetails,
    copyLinkTooltip,
    isViewFormModalOpen,
    toggleViewFormModal,
    onCopyS3Link,
    onPreviewFile,
    onClosePreview
  } = useResultMeta({ result, isFile, bucket, path, contents, isReadyForDownload, resultId })

  const { studyId, requestStatus, reviewSubStatus, dataAccessRequestDetail, sharing } =
    useDataAccessRequestRelatedSection({ resultId, result })

  const fileSubtitle = file ? `${formattedFileSize}, last modified on ${formattedLastModified}` : undefined
  const areFileActionsDisabled = isApprovalRestricted || isFileViewDisabled
  const canPreviewFile = !areFileActionsDisabled && isFile && !!file && !isCompressedFileName(file.name)

  return (
    <>
      <Card data-testid="result-meta">
        <styles.CardContent>
          {isFileChanged && <Alert type="info">{fileChangedAlertMessage}</Alert>}
          {isDifferentRequester && <Alert type="warning">{differentRequesterAlertMessage}</Alert>}

          {isDataAccessRequestResult ? (
            <ParticipantFileDetails
              isDownloadDisabled={isParticipantFileDownloadDisabled}
              isDownloadLoading={isParticipantFileDownloadLoading}
              participantFile={participantFile}
              resultId={resultId}
            />
          ) : (
            (displayName || file) && (
              <FileDetails
                actionsPlaceholder={isFileActionsLoading ? <FileActionsSkeleton /> : undefined}
                copyLinkTooltip={copyLinkTooltip}
                displayName={displayName}
                downloadDetails={areFileActionsDisabled ? undefined : downloadDetails}
                isFile={isFile}
                onCopyS3Link={areFileActionsDisabled ? undefined : onCopyS3Link}
                onPreviewFile={canPreviewFile ? onPreviewFile : undefined}
                subtitle={fileSubtitle}
              />
            )
          )}

          {isImportRequest && importRequestFileName && (
            <styles.ItemRow>
              <styles.ItemIcon data-testid="import-request-file-icon">
                <icons.File />
              </styles.ItemIcon>
              <styles.ItemInfo>
                <styles.ItemTitle>{importRequestFileName}</styles.ItemTitle>
                {importRequestFileSize && <styles.ItemSubtitle>{importRequestFileSize}</styles.ItemSubtitle>}
              </styles.ItemInfo>
            </styles.ItemRow>
          )}

          {isImportRequest && (
            <ImportRequestScanDetails
              scanResult={importRequestScanResult}
              scanStartedAt={importRequestScanStartedAt}
              scanCompletedAt={importRequestScanCompletedAt}
              scanStatusCode={importRequestScanStatusCode}
              isLoading={isImportRequestScanLoading}
            />
          )}

          {isDataAccessRequestResult ? (
            <DataAccessRequestFormCard
              result={result}
              onViewForm={toggleViewFormModal}
              studyId={studyId}
              requestStatus={requestStatus}
              reviewSubStatus={reviewSubStatus}
              dataAccessRequestDetail={dataAccessRequestDetail}
              sharing={sharing}
            />
          ) : (
            customFormSubmission && (
              <FormSubmissionPreview formSubmission={customFormSubmission} onViewForm={toggleViewFormModal} />
            )
          )}

          {!isDataAccessRequestResult && (isAutomatedChecksToReRunVisible || isAutomatedChecksSkippedVisible) && (
            <AutomatedChecksReRun
              resultId={resultId}
              automatedChecks={result.automatedChecks}
              fileChangeStatus={result.fileChangeStatus}
              validationStatus={result.validationStatus}
              isAutomaticApproved={result.isAutomaticApproved}
              workspaceName={result.workspaceName}
            />
          )}
        </styles.CardContent>
      </Card>

      <Modal
        onCancel={onClosePreview}
        open={!!selectedFileToPreview && !areFileActionsDisabled}
        title={`View details: ${selectedFileToPreview?.name}`}
        size="large"
        closable
      >
        {selectedFileToPreview && bucket && path && (
          <PreviewModalContent>
            <FileContent file={selectedFileToPreview} resultId={resultId} bucket={bucket} fileKey={previewFileKey} />
          </PreviewModalContent>
        )}
      </Modal>

      <ViewFormModal
        open={isViewFormModalOpen}
        onClose={toggleViewFormModal}
        formSubmission={customFormSubmission}
        resultId={resultId}
        resultBucket={bucket}
      />
    </>
  )
}

export default ResultMeta
